import React from 'react';
import { Box } from '@mui/material';

export type ChangeDisplayMode = 'difference' | 'percentage';

// The primary value stays unchanged. The compact secondary line is either the
// year-over-year absolute difference or the relative percentage change.
export function ValueWithChange({
    current,
    previous,
    format,
    showChange = true,
    changeDisplay = 'difference',
}: {
    current: number;
    previous: number;
    format: (value: number) => string;
    showChange?: boolean;
    changeDisplay?: ChangeDisplayMode;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
            }}
        >
            <Box component={'span'}>{format(current)}</Box>
            {showChange ? (
                <ChangeLabel
                    current={current}
                    previous={previous}
                    format={format}
                    changeDisplay={changeDisplay}
                />
            ) : null}
        </Box>
    );
}

function ChangeLabel({
    current,
    previous,
    format,
    changeDisplay,
}: {
    current: number;
    previous: number;
    format: (value: number) => string;
    changeDisplay: ChangeDisplayMode;
}) {
    if (!previous && !current) return null;

    const net = current - previous;
    const color =
        net > 0 ? 'success.main' : net < 0 ? 'error.main' : 'text.secondary';
    let comparisonText: string;

    if (changeDisplay === 'percentage') {
        // A percentage change from zero is undefined, so retain a single
        // readable label instead of inventing an infinite percentage.
        comparisonText = previous
            ? `${net > 0 ? '+' : ''}${((net / previous) * 100).toFixed(1)}%`
            : 'nuevo';
    } else {
        comparisonText = (net > 0 ? '+' : '') + format(net);
    }

    return (
        <Box component={'span'} sx={{ fontSize: '0.7rem', color }}>
            {comparisonText}
        </Box>
    );
}
